// src/config/claimFlow.js
//
// Definición del proceso de un reclamo y de cada cheque, como DATOS — no
// como pantallas. Fuente: flujo operativo enviado por el cliente
// (2026-09-22) + PLAN_ESTRUCTURA_PASOS.md. Cambiar el proceso (orden,
// nombres, quién actúa, módulos de cada paso) se hace acá, sin tocar el
// motor (flowService.js) ni los componentes.
//
// Cada paso:
//   id       estable — se guarda en Firestore, NO renombrar sin migrar.
//   phase    1-4 (reclamo) o 'A'/'B' (cheque); 'end' para estados finales.
//   kind     'task' (alguien hace algo) | 'decision' (el PA elige camino)
//            | 'wait' (depende de un tercero) | 'final'.
//   actor    quién lo completa: 'csr' | 'pa' | 'client' | 'insurer' |
//            'bank' | 'attorney' | 'system'. Solo 'pa' lo puede completar
//            el ajustador; todo lo demás lo registra el equipo CSR.
//   next     id del siguiente paso (task/wait), u
//   options  [{ id, label, next }] para las decisiones y bifurcaciones.
//   modules  espacios de la pantalla del paso. Los que aún no existen se
//            muestran como "Próximamente" (ver components/Flow/modules).
//   optional el paso puede saltarse con "Completar" aunque no aplique.
//   fields   datos que se cargan en el paso (módulo `stepForm`):
//            { key, label, type, target: 'claim' | 'step', options? }
//            target 'claim' = se guarda en el reclamo (lo usan LOR/SPOL y el
//            resto de la ficha); 'step' = dato propio del paso (stepData).

const l = (es, en) => ({ es, en });
const f = (key, es, en, type = 'text', target = 'step', options) => ({ key, label: l(es, en), type, target, ...(options ? { options } : {}) });
const opt = (value, es, en) => ({ value, label: l(es, en) });

export const COVERAGE_DECISIONS = [
  opt('approved', 'Aprobada', 'Approved'),
  opt('partial_denial', 'Parcialmente negada', 'Partial denial'),
  opt('below_deductible', 'Bajo el deducible', 'Below deductible'),
  opt('denied', 'Negada', 'Denied')
];

// Estados finales alcanzables desde cualquier paso (acción "Mover a…").
// Un reclamo terminado queda en el panel con su estado final: 'closed' se
// muestra como "Negociado" (id estable: no renombrar sin migrar).
export const FINAL_STEPS = ['closed', 'withdrawn'];

export const CLAIM_PHASES = [
  { id: 1, label: l('Intake y reporte', 'Intake & report') },
  { id: 2, label: l('Inspección y documentación', 'Inspection & documentation') },
  { id: 3, label: l('Cobertura y ruta', 'Coverage & route') },
  { id: 4, label: l('Cobro y cierre', 'Collection & close') }
];

export const CLAIM_FLOW = {
  type: 'claim',
  start: 'prospect',
  steps: [
    // ── FASE 1: INTAKE Y REPORTE ──────────────────────────────────────
    { id: 'prospect', phase: 1, kind: 'task', actor: 'csr', label: l('Prospecto', 'Prospect'),
      next: 'policy_review', modules: ['leadForm', 'notes', 'documents'] },
    // La póliza suele llegar con el lead; revisarla antes de que el PA decida
    // si se reporta. Opcional: si todavía no está, se sigue y se revisa luego
    // (la tarjeta del paso se puede abrir en cualquier momento).
    { id: 'policy_review', phase: 1, kind: 'task', actor: 'csr', optional: true, label: l('Revisión de póliza', 'Policy review'),
      next: 'lor_signature', modules: ['policyReview', 'documents', 'notes'] },
    { id: 'lor_signature', phase: 1, kind: 'wait', actor: 'client', label: l('Firma de la LOR', 'LOR signature'),
      next: 'pa_approval',
      // Fechas de envío y firma: las registra el módulo `signature` (DocumentSignature).
      modules: ['signature', 'documents'] },
    { id: 'pa_approval', phase: 1, kind: 'decision', actor: 'pa', label: l('Autorización del PA para reportar', 'PA approval to report'),
      options: [
        { id: 'report', label: l('Reportar al seguro', 'Report to insurance'), next: 'report_to_insurer' },
        { id: 'discard', label: l('Descartar', 'Discard'), next: 'withdrawn' }
      ],
      modules: ['decision', 'notes'] },
    { id: 'report_to_insurer', phase: 1, kind: 'task', actor: 'csr', label: l('Reporte al seguro', 'Report to insurance'),
      next: 'lor_sent',
      fields: [
        f('claimNumber', 'Número de reclamo (Claim #)', 'Claim number', 'text', 'claim'),
        f('insuranceCompanyName', 'Aseguradora', 'Insurance company', 'text', 'claim'),
        f('policyNumber', 'Número de póliza', 'Policy number', 'text', 'claim'),
        f('deskAdjusterName', 'Desk Adjuster (DA)', 'Desk Adjuster (DA)', 'text', 'claim'),
        f('deskAdjusterEmail', 'Correo del DA', 'DA email', 'email', 'claim'),
        f('deskAdjusterPhone', 'Teléfono del DA', 'DA phone', 'tel', 'claim'),
        f('reportedDate', 'Fecha del reporte', 'Report date', 'date')
      ],
      modules: ['stepForm', 'notes'] },
    { id: 'lor_sent', phase: 1, kind: 'task', actor: 'csr', label: l('LOR enviada al seguro', 'LOR sent to insurance'),
      next: 'fa_contact',
      fields: [f('lorSentToInsurerDate', 'LOR enviada al seguro', 'LOR sent to insurance', 'date')],
      modules: ['emailInsurer', 'stepForm', 'checklist', 'documents'] },

    // ── FASE 2: INSPECCIÓN Y DOCUMENTACIÓN ─────────────────────────────
    { id: 'fa_contact', phase: 2, kind: 'wait', actor: 'insurer', label: l('Contacto del FA', 'Field adjuster contact'),
      next: 'inspection',
      fields: [
        f('insuranceAdjusterName', 'Field Adjuster (FA)', 'Field Adjuster (FA)', 'text', 'claim'),
        f('insuranceAdjusterPhone', 'Teléfono del FA', 'FA phone', 'tel', 'claim'),
        f('insuranceAdjusterEmail', 'Correo del FA', 'FA email', 'email', 'claim'),
        f('faContactDate', 'Fecha de contacto del FA', 'FA contact date', 'date')
      ],
      modules: ['stepForm'] },
    { id: 'inspection', phase: 2, kind: 'task', actor: 'csr', label: l('Inspección', 'Inspection'),
      next: 'evidence',
      fields: [f('inspectionDate', 'Fecha de la inspección', 'Inspection date', 'date')],
      modules: ['schedule', 'stepForm', 'notes'] },
    { id: 'evidence', phase: 2, kind: 'task', actor: 'csr', label: l('Recolección de evidencia', 'Evidence collection'),
      next: 'rs_pol', modules: ['documents', 'checklist', 'scan'] },
    { id: 'rs_pol', phase: 2, kind: 'task', actor: 'csr', optional: true, label: l('RS / POL', 'RS / POL'),
      next: 'estimate',
      fields: [
        f('rsDate', 'Fecha del Recorded Statement (si aplica)', 'Recorded Statement date (if any)', 'date'),
        f('rcv', 'RCV (Replacement Cost Value, del estimado)', 'RCV (Replacement Cost Value, from estimate)', 'money'),
        f('actualCashValue', 'ACV (Actual Cash Value, del estimado)', 'ACV (Actual Cash Value, from estimate)', 'money'),
        f('emsVendors', 'EMS / Vendors (total)', 'EMS / Vendors (total)', 'money'),
        f('polSentDate', 'POL enviada al seguro', 'POL sent to insurance', 'date')
      ],
      modules: ['stepForm', 'polGenerator', 'schedule', 'documents'] },
    { id: 'estimate', phase: 2, kind: 'decision', actor: 'pa', label: l('Envío del estimado', 'Estimate submission'),
      options: [
        { id: 'before_coverage', label: l('Enviar antes de la cobertura', 'Send before coverage'), next: 'awaiting_coverage' },
        { id: 'after_coverage', label: l('Enviar después de la cobertura', 'Send after coverage'), next: 'awaiting_coverage' }
      ],

      fields: [f('estimateSentDate', 'Estimado enviado al seguro', 'Estimate sent to insurance', 'date')],
      modules: ['decision', 'stepForm', 'documents'] },
    { id: 'awaiting_coverage', phase: 2, kind: 'wait', actor: 'insurer', label: l('Esperando cobertura', 'Awaiting coverage'),
      next: 'coverage_received', modules: ['notes'] },

    // ── FASE 3: COBERTURA Y RUTA ───────────────────────────────────────
    { id: 'coverage_received', phase: 3, kind: 'task', actor: 'csr', label: l('Decisión de cobertura recibida', 'Coverage decision received'),
      next: 'pa_route',
      fields: [
        f('coverageDecision', 'Decisión de cobertura', 'Coverage decision', 'select', 'claim', COVERAGE_DECISIONS),
        f('coverageDecisionDate', 'Fecha de la decisión', 'Decision date', 'date'),
        f('coverageAmount', 'Monto aprobado por el seguro', 'Amount approved by insurance', 'money'),
        // Del estimado del seguro: se pide cuando el cliente cobre y repare (utils/recoverableDepreciation.js).
        f('recoverableDepreciation', 'Depreciación recuperable (RD) del estimado', 'Recoverable Depreciation (RD) from the estimate', 'money', 'claim')
      ],
      modules: ['stepForm', 'documents'] },
    { id: 'pa_route', phase: 3, kind: 'decision', actor: 'pa', label: l('Ruta del PA', 'PA route'),
      options: [
        { id: 'accept', label: l('Aceptar y cobrar', 'Accept and collect'), next: 'collection' },
        { id: 'negotiate', label: l('Negociación', 'Negotiation'), next: 'negotiation' },
        { id: 'appraisal', label: l('Appraisal', 'Appraisal'), next: 'appraisal' },
        { id: 'mediation', label: l('Mediación', 'Mediation'), next: 'mediation' },
        { id: 'attorney', label: l('Enviar a abogado', 'Send to attorney'), next: 'attorney_noi' },
        { id: 'close', label: l('Cerrar como negociado', 'Close as settled'), next: 'closed' }
      ],
      modules: ['decision', 'notes'] },
    { id: 'negotiation', phase: 3, kind: 'task', actor: 'pa', label: l('Negociación', 'Negotiation'),
      options: [
        { id: 'agreement', label: l('Acuerdo alcanzado', 'Agreement reached'), next: 'collection' },
        { id: 'escalate', label: l('Escalar (otra ruta)', 'Escalate (other route)'), next: 'pa_route' }
      ],

      fields: [f('settlementAmount', 'Monto acordado', 'Agreed amount', 'money')],
      modules: ['stepForm', 'notes'] },
    { id: 'appraisal', phase: 3, kind: 'task', actor: 'pa', label: l('Appraisal (→ Umpire)', 'Appraisal (→ Umpire)'),
      options: [
        { id: 'agreement', label: l('Acuerdo / laudo', 'Agreement / award'), next: 'collection' },
        { id: 'escalate', label: l('Escalar (otra ruta)', 'Escalate (other route)'), next: 'pa_route' }
      ],

      fields: [
        f('appraiserName', 'Tasador (appraiser)', 'Appraiser', 'text'),
        f('umpireName', 'Umpire (si aplica)', 'Umpire (if any)', 'text'),
        f('appraisalCost', 'Costo a descontar del settlement', 'Cost deducted from settlement', 'money')
      ],
      modules: ['stepForm', 'schedule', 'notes'] },
    { id: 'mediation', phase: 3, kind: 'task', actor: 'csr', label: l('Mediación DFS (→ Inpass)', 'DFS mediation (→ Impasse)'),
      options: [
        { id: 'agreement', label: l('Acuerdo', 'Agreement'), next: 'collection' },
        { id: 'impasse', label: l('Inpass → abogado', 'Impasse → attorney'), next: 'attorney_noi' }
      ],

      fields: [
        f('mediationRequestDate', 'Solicitud en el portal DFS', 'DFS portal request', 'date'),
        f('mediationCost', 'Costo a descontar (normalmente $500)', 'Cost deducted (usually $500)', 'money')
      ],
      modules: ['stepForm', 'schedule', 'notes'] },
    { id: 'attorney_noi', phase: 3, kind: 'wait', actor: 'attorney', label: l('Abogado / NOI', 'Attorney / NOI'),
      options: [
        { id: 'agreement', label: l('Acuerdo', 'Agreement'), next: 'collection' },
        { id: 'litigation', label: l('Pasar a litigio', 'Move to litigation'), next: 'litigation' }
      ],

      fields: [
        f('attorneyName', 'Abogado / firma', 'Attorney / firm', 'text', 'claim'),
        f('attorneyEmail', 'Correo del abogado', 'Attorney email', 'email', 'claim'),
        f('fileSentToAttorneyDate', 'Expediente enviado al abogado', 'File sent to attorney', 'date'),
        f('noiDate', 'Fecha del NOI (Notice of Intent)', 'NOI date', 'date')
      ],
      modules: ['stepForm', 'attorneyExport', 'notes'] },
    { id: 'litigation', phase: 3, kind: 'wait', actor: 'attorney', label: l('Litigio', 'Litigation'),
      options: [
        { id: 'agreement', label: l('Acuerdo / sentencia', 'Agreement / judgment'), next: 'collection' },
        { id: 'close', label: l('Cerrar como negociado', 'Close as settled'), next: 'closed' }
      ],

      fields: [
        f('suitFiledDate', 'Demanda presentada', 'Suit filed', 'date'),
        f('caseNumber', 'Número de caso en la corte', 'Court case number', 'text')
      ],
      modules: ['stepForm', 'notes', 'documents'] },

    // ── FASE 4: COBRO Y CIERRE ─────────────────────────────────────────
    { id: 'collection', phase: 4, kind: 'wait', actor: 'csr', label: l('Cobro en curso', 'Collection in progress'),
      options: [
        { id: 'rcv', label: l('Queda depreciación recuperable', 'Recoverable depreciation pending'), next: 'rcv' },
        { id: 'close', label: l('Cerrar como negociado', 'Close as settled'), next: 'closed' }
      ],
      modules: ['checks', 'documents', 'notes'] },
    { id: 'rcv', phase: 4, kind: 'task', actor: 'csr', optional: true, label: l('Depreciación recuperable (RCV)', 'Recoverable depreciation (RCV)'),
      fields: [
        f('recoverableDepreciation', 'Depreciación recuperable (RD) del estimado', 'Recoverable Depreciation (RD) from the estimate', 'money', 'claim'),
        f('rdRequestedDate', 'RD pedida al seguro (pruebas enviadas)', 'RD requested from insurance (proof sent)', 'date', 'claim')
      ],
      options: [
        { id: 'new_check', label: l('Llegó nuevo cheque', 'New check arrived'), next: 'collection' },
        { id: 'close', label: l('Cerrar como negociado', 'Close as settled'), next: 'closed' }
      ],
      modules: ['stepForm', 'checks', 'documents', 'notes'] },

    // ── ESTADOS FINALES ────────────────────────────────────────────────
    { id: 'closed', phase: 'end', kind: 'final', actor: 'csr', label: l('Negociado', 'Settled'), modules: ['notes'] },
    { id: 'withdrawn', phase: 'end', kind: 'final', actor: 'csr', label: l('Retirado / perdido', 'Withdrawn / lost'), modules: ['notes'] }
  ]
};

// Flujo de cada cheque — Fase 4 del documento del cliente. El cheque existe
// desde que el seguro lo emite; cada uno avanza por su cuenta (un reclamo
// puede tener varios: inicial, suplemental, RCV…).
//   Tramo A (documento del cliente): emitido → ruta de endoso (decide el PA)
//     → IINK | endoso de mortgage | directo → depositado → breakdown (PA)
//     → Closing Statement firmado → desembolso al cliente.
//   Tramo B (interno de CSR, no está en el documento): cobro de la comisión
//     dentro de la tarjeta del cheque (CsrCollection.jsx): valores reales,
//     backup del cheque, pagos y archivo.
export const CHECK_PHASES = [
  { id: 'A', label: l('Cheque del seguro', 'Insurance check') },
  { id: 'B', label: l('Cobro CSR (interno)', 'CSR collection (internal)') }
];

export const CHECK_FLOW = {
  type: 'check',
  start: 'issued',
  steps: [
    // ── TRAMO A: CHEQUE DEL SEGURO ─────────────────────────────────────
    { id: 'issued', phase: 'A', kind: 'wait', actor: 'csr', label: l('Cheque emitido · en camino', 'Check issued · in transit'),
      next: 'endorsement_route', modules: ['checkDetails'] },
    { id: 'endorsement_route', phase: 'A', kind: 'decision', actor: 'pa', label: l('Ruta de endoso (decide el PA)', 'Endorsement route (PA decides)'),
      options: [
        { id: 'iink', label: l('IINK (100 % digital)', 'IINK (fully digital)'), next: 'iink' },
        { id: 'mortgage', label: l('Endoso de mortgage', 'Mortgage endorsement'), next: 'mortgage_endorsement' },
        { id: 'direct', label: l('Directo (PA)', 'Direct (PA)'), next: 'deposited' }
      ],
      modules: ['decision'] },
    { id: 'iink', phase: 'A', kind: 'wait', actor: 'pa', label: l('Endoso digital IINK', 'IINK digital endorsement'),
      next: 'deposited', fields: [f('iinkReference', 'Referencia de IINK (opcional)', 'IINK reference (optional)', 'text')],
      modules: ['stepForm'] },
    { id: 'mortgage_endorsement', phase: 'A', kind: 'task', actor: 'csr', label: l('Endoso de mortgage', 'Mortgage endorsement'),
      next: 'deposited',
      fields: [
        f('bankCallDate', 'Fecha de la llamada al banco', 'Bank call date', 'date'),
        f('mailingAddress', 'Dirección de envío que dio el banco', 'Bank mailing address', 'text'),
        f('sentDate', 'Fecha de envío del cheque al banco', 'Date sent to the bank', 'date'),
        f('trackingNumber', 'Tracking number', 'Tracking number', 'text')
      ],
      modules: ['stepForm', 'mortgageDocs'] },
    { id: 'deposited', phase: 'A', kind: 'task', actor: 'pa', label: l('Depositado en la cuenta del PA', 'Deposited in the PA account'),
      next: 'breakdown', fields: [f('depositDate', 'Fecha del depósito', 'Deposit date', 'date')], modules: ['stepForm'] },
    { id: 'breakdown', phase: 'A', kind: 'task', actor: 'pa', label: l('Breakdown (lo define el PA)', 'Breakdown (PA defines it)'),
      next: 'closing_statement', modules: ['closing'] },
    { id: 'closing_statement', phase: 'A', kind: 'wait', actor: 'client', label: l('Closing Statement al cliente', 'Closing Statement to client'),
      next: 'disbursed', modules: ['closing'] },
    { id: 'disbursed', phase: 'A', kind: 'task', actor: 'pa', label: l('Desembolso al cliente', 'Disbursed to client'),
      next: 'real_values', fields: [f('disbursedDate', 'Fecha del desembolso', 'Disbursement date', 'date')], modules: ['stepForm'] },

    // ── TRAMO B: COBRO CSR (interno) ───────────────────────────────────
    { id: 'real_values', phase: 'B', kind: 'task', actor: 'csr', label: l('Valores reales + backup', 'Real values + backup'),
      next: 'csr_payment', modules: ['receiveCheck'] },
    // Lo paga el PA, pero lo registra el equipo CSR al conciliar el Zelle.
    { id: 'csr_payment', phase: 'B', kind: 'wait', actor: 'csr', label: l('Pago de comisión', 'Commission payment'),
      next: 'check_archived', modules: ['checkPayment'] },
    // id histórico ('check_archived'): el cheque terminó, con la comisión cobrada.
    // Muestra el cobro (solo consulta) para no perder "Copiar mensaje de pagado",
    // el invoice y la fecha del pago al pasar aquí solo tras el pago completo.
    { id: 'check_archived', phase: 'B', kind: 'final', actor: 'csr', label: l('Comisión cobrada', 'Commission collected'), modules: ['checkPayment'] }
  ]
};

// Qué hay que hacer en cada paso, en una línea (documento de pasos del cliente).
export const STEP_HELP = {
  claim: {
    prospect: l('Lead recibido: carga nombre, dirección, seguro, póliza, mortgage, DOL, deducible y daños. Queda en espera de revisión.', 'Lead received: enter name, address, insurance, policy, mortgage, DOL, deductible and damages. It waits for review.'),
    policy_review: l('Opcional: sube la póliza y revisa coberturas, deducibles y exclusiones antes de que decida el PA.', 'Optional: upload the policy and review coverages, deductibles and exclusions before the PA decides.'),
    pa_approval: l('El PA decide si se reporta el reclamo al seguro.', 'The PA decides whether to report the claim.'),
    report_to_insurer: l('Llama al seguro, describe los daños y consigue el Claim # y el correo del Desk Adjuster.', 'Call the insurance company, describe the damage and get the claim # and the Desk Adjuster email.'),
    lor_signature: l('Genera la LOR, mándala al cliente para firma y, cuando firme, sube aquí la LOR firmada.', 'Generate the LOR, send it to the client to sign and upload the signed LOR here.'),
    lor_sent: l('Envía la LOR firmada al seguro por correo. Tienes 72 horas desde la firma; si vencen, se genera y firma de nuevo.', 'Email the signed LOR to the insurance company within 72 hours of signing; otherwise it must be signed again.'),
    fa_contact: l('El Field Adjuster debe contactar en 1-2 días hábiles. Si pasan 48 h sin contacto, haz seguimiento.', 'The Field Adjuster should call within 1-2 business days. Follow up after 48 h.'),
    inspection: l('Coordina fecha y hora de la inspección entre el PA, el cliente y el FA.', 'Coordinate the inspection between the PA, the client and the FA.'),
    evidence: l('Pide y sube invoices y reportes de mitigación, remediación, carpa (tarp/mold), fotos y videos.', 'Request and upload mitigation, remediation, tarp/mold invoices and reports, photos and videos.'),
    rs_pol: l('Solo si el seguro lo exige: agenda el Recorded Statement y prepara la Proof of Loss.', 'Only if required: schedule the Recorded Statement and prepare the Proof of Loss.'),
    estimate: l('El PA decide si el estimado va al seguro antes o después de la decisión de cobertura.', 'The PA decides whether the estimate goes before or after the coverage decision.'),
    awaiting_coverage: l('Esperando que el Desk Adjuster emita la decisión de cobertura.', 'Waiting for the Desk Adjuster coverage decision.'),
    coverage_received: l('Registra la decisión de cobertura del DA.', 'Record the DA coverage decision.'),
    pa_route: l('El PA elige: pago suplemental (negociación), appraisal, mediación, abogado o cerrar.', 'The PA chooses: supplemental (negotiation), appraisal, mediation, attorney or close.'),
    negotiation: l('Pago suplemental: el PA negocia directamente con el seguro.', 'Supplemental payment: the PA negotiates directly.'),
    appraisal: l('Se disputa la decisión técnica. Sin acuerdo entre tasadores pasa a Umpire. El costo se descuenta del settlement.', 'Technical dispute. Without agreement it goes to an Umpire. The cost is deducted from the settlement.'),
    mediation: l('Agenda la mediación en el portal del DFS. $500 descontados del settlement. Sin acuerdo termina en Inpass.', 'Schedule mediation on the DFS portal. $500 deducted from the settlement. No agreement → impasse.'),
    attorney_noi: l('Envía el expediente completo al abogado. Desde el NOI se cuentan 45 días antes de poder ir a litigio.', 'Send the full file to the attorney. 45 days from the NOI before litigation.'),
    litigation: l('Demanda presentada: discovery y depositions.', 'Suit filed: discovery and depositions.'),
    collection: l('Registra cada cheque que emite el seguro y avanza cada uno: ruta de endoso, depósito, breakdown, Closing y desembolso.', 'Register each check the insurance company issues and move each one forward: endorsement, deposit, breakdown, Closing, disbursement.'),
    rcv: l('Cuando el cliente cobre y repare, envía las pruebas (pago, fotos, invoices) al seguro para pedir la depreciación recuperable y anota la fecha. El cheque que llegue se registra con tipo "Recoverable Depreciation".', 'Once the client is paid and repaired, send proof (payment, photos, invoices) to request the recoverable depreciation and record the date. Log the check that arrives with type "Recoverable Depreciation".')
  },
  check: {
    issued: l('El seguro emitió el cheque: registra monto, número y fecha. Suele llegar en 7-10 días hábiles.', 'The insurance company issued the check: enter amount, number and date. It usually arrives in 7-10 business days.'),
    endorsement_route: l('El PA decide cómo se endosa: IINK (digital), endoso de mortgage o directo (solo < $10k o sin mortgage).', 'The PA decides: IINK (digital), mortgage endorsement or direct (only < $10k or no mortgage).'),
    iink: l('Endoso 100 % digital gestionado por el PA en IINK.', 'Fully digital endorsement handled by the PA in IINK.'),
    mortgage_endorsement: l('Llama al banco, pide la dirección de envío y registra el tracking. Más de $40,000: sube los 3 documentos obligatorios. Vuelve en 10-12 días hábiles.', 'Call the bank, get the mailing address and tracking. Over $40,000: upload the 3 required documents. Returns in 10-12 business days.'),
    deposited: l('El PA deposita el cheque en su cuenta.', 'The PA deposits the check.'),
    breakdown: l('El PA define la distribución del dinero (Breakdown): honorario y costos a descontar.', 'The PA defines the distribution (Breakdown): fee and deductible costs.'),
    closing_statement: l('Arma el Closing Statement y pulsa "Enviar a firmar" y, cuando firme, sube el PDF firmado.', 'Build the Closing Statement and press "Send for signature" and, once signed, upload the signed PDF.'),
    disbursed: l('El PA le entrega los fondos al cliente.', 'The PA disburses the funds to the client.'),
    real_values: l('Cobro interno de CSR: confirma los valores reales y sube el backup del cheque.', 'Internal CSR collection: confirm the real values and upload the check backup.'),
    csr_payment: l('Cobro interno de CSR: registra el pago de la comisión.', 'Internal CSR collection: record the commission payment.')
  }
};

export const FLOWS = { claim: CLAIM_FLOW, check: CHECK_FLOW };
